import { getDehydratedState, prefetchSfxSearch } from '@/utils/search/ssr';
import * as Sentry from '@sentry/nextjs';
import { QueryClient } from '@tanstack/react-query';
import { safeRedirectPath } from '@/lib/auth/safeRedirectPath';
import { setAuthAwareCacheHeaders } from '@/utils/pages/cache-headers';
import { getPageData, getPageNumberFromSlug } from '@/utils/pages';
import { isPaginationPath } from '@/utils/pagination';
import { getPrimaryHeading } from '@/components/PageHeader/utils';
import { prefetchPageCarousels } from '@/hooks/useTypesenseCarousels';
import BrowseSfxPage, { type BrowseTagPageParams, type SfxPageProps } from '@/components/_pages/BrowseSfxPage';
import type { CategoryHeader } from '@/utils/pages/api';
import type { GetServerSideProps } from 'next';
export const getServerSideProps: GetServerSideProps<SfxPageProps, BrowseTagPageParams> = async ({
  params,
  res,
  req,
  query
}) => {
  const {
    categorySlug: slug
  } = params ?? {};
  const authState = req.headers['x-auth-state'];
  if (!Array.isArray(slug)) {
    return {
      notFound: true
    };
  }
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: Number.POSITIVE_INFINITY
      }
    }
  });

  // Get collection context from query params (set by middleware)
  const {
    _page_type,
    _original_url,
    ...restQuery
  } = query || {};

  // Use query params first, fall back to headers (for backwards compatibility)
  const pageType = _page_type as string || req.headers['x-page-type'] as string | undefined;
  const originalUrl = _original_url as string || req.headers['x-original-url'] as string | undefined;
  if (pageType) {
    res.setHeader('x-page-type', pageType);
  }
  if (authState) {
    res.setHeader('x-auth-state', authState);
  }

  // Determine the correct URL to use based on page type
  // For collections, use the original collection URL for API calls
  // For categories, use the category URL
  const isCollectionPageType = pageType === 'collection';
  const baseUrl = isCollectionPageType ? originalUrl || `/sfx/collection/${slug?.join('/')}` : `/sfx/category/${slug?.join('/')}`;

  // Remove the page number from the slug
  const fullSlug = baseUrl.replace(/\/page\/\d+$/, '');

  // Category pages use infinite scroll only - pagination paths are not supported
  if (isPaginationPath(slug)) {
    res.setHeader('Cache-Control', 'private, no-cache, no-store, must-revalidate');
    return {
      notFound: true
    };
  }
  try {
    const result = await getPageData<CategoryHeader>(slug, {
      pageType: typeof pageType === 'string' ? pageType : undefined,
      assetType: 'sfx',
      pageSlug: isCollectionPageType ? ['sfx', 'collection', ...slug] : ['sfx', 'category', ...slug],
      source: fullSlug,
      shouldFetchTracks: false,
      includeComprehensiveListings: authState !== 'authenticated'
    });

    // Handle redirect case
    if ('redirect' in result) {
      return result;
    }

    // Handle notFound case
    if ('notFound' in result) {
      res.setHeader('Cache-Control', 'private, no-cache, no-store, must-revalidate');
      return result;
    }

    // At this point, result must have props
    if (!result.props) {
      Sentry.captureMessage('Missing props in getPageData result');
      res.setHeader('Cache-Control', 'private, no-cache, no-store, must-revalidate');
      return {
        notFound: true
      };
    }

    // Validate required fields in pageData
    const {
      pageData
    } = result.props;

    // A valid tag/style page accessed from a non-canonical URL — e.g. a
    // sub-category reached via its bare slug instead of the canonical nested
    // path. Redirect to the canonical URL the API returned rather than 404ing,
    // which preserves the traffic and consolidates SEO onto one URL.
    if (!isCollectionPageType && pageData.url !== fullSlug) {
      const canonicalUrl = safeRedirectPath(pageData.url);
      if (canonicalUrl) {
        return {
          redirect: {
            destination: canonicalUrl,
            permanent: true
          }
        };
      }
      Sentry.captureMessage('Valid page has an unusable canonical URL');
      res.setHeader('Cache-Control', 'private, no-cache, no-store, must-revalidate');
      return {
        notFound: true
      };
    }
    if (!(pageData.name && pageData.id)) {
      Sentry.withScope(scope => {
        scope.setExtra('params', params);
        scope.setExtra('invalidPageData', JSON.stringify(pageData));
        Sentry.captureMessage('Invalid page data received from API');
      });
      res.setHeader('Cache-Control', 'private, no-cache, no-store, must-revalidate');
      return {
        notFound: true
      };
    }

    // Cache the page for 1 day, but allow stale while revalidating.
    // Only set on successful responses to avoid caching 404s/errors, and only
    // for logged-out visitors — every logged-in one shares the `?_auth=1`
    // cache key, so a `public` response there would leak the first
    // subscriber's page to the rest.
    setAuthAwareCacheHeaders({
      query,
      req,
      res
    }, {
      cacheTagPath: fullSlug
    });

    // Prefetch search results
    const {
      slug: cleanSlug
    } = getPageNumberFromSlug(slug);
    const categorySlug = cleanSlug.join('/');
    let totalCount: number | undefined;
    try {
      // Extract initial query and filter parameters from URL for consistent hydration
      // This helps ensure SSR prefetched data matches what client will initially render
      const initialQuery = typeof restQuery.q === 'string' ? restQuery.q : '';
      const initialFilters = typeof restQuery.filter === 'string' ? restQuery.filter : '';
      const prefetchResult = await prefetchSfxSearch({
        queryClient,
        categorySlug,
        // Keep the original categorySlug for backward compatibility
        contextPath: fullSlug,
        slug: fullSlug,
        // Pass the full slug to match the hook
        initialQuery,
        // Pass initial search query from URL
        initialFilters,
        // Pass initial filters from URL
        ssrContext: {
          params,
          res,
          req,
          query,
          resolvedUrl: req.url || ''
        } // Pass SSR context for auth
      });
      totalCount = prefetchResult?.totalCount;
    } catch {
      // Continue without prefetch
    }

    // Prefetch header carousels (category pages only) so they hydrate on first
    // paint instead of flashing in after a client-side fetch.
    if (!isCollectionPageType) {
      try {
        await prefetchPageCarousels(queryClient, 'sfx', pageData);
      } catch {
        // Continue without carousel prefetch
      }
    }
    const pageTitle = getPrimaryHeading({
      data: pageData,
      userIsAuthenticated: typeof authState === 'string' ? authState === 'authenticated' : false
    });
    return {
      props: {
        ...result.props,
        pageTitle,
        dehydratedState: getDehydratedState(queryClient),
        contextPath: fullSlug,
        authState: typeof authState === 'string' ? authState : 'anonymous',
        totalCount
      }
    };
  } catch (error) {
    Sentry.withScope(scope => {
      scope.setExtra('params', params);
      scope.setExtra('fullSlug', fullSlug);
      Sentry.captureException(error);
    });
    res.setHeader('Cache-Control', 'private, no-cache, no-store, must-revalidate');
    return {
      notFound: true
    };
  }
};
export default BrowseSfxPage;